import { useCallback, useId, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, toast } from '@/ui';
import { postAcceptKvkk, postAcceptUsage } from '@/api/iam';
import { useSessionStore } from '@/auth/session.store';
import { zoneHome } from '@/auth/zone-home';
import { authErrorMessage } from '@/features/auth/error-message';
import { LegalReader } from './LegalReader';
import { KVKK_TEXT, LEGAL_COPY, USAGE_AGREEMENT_TEXT } from './legal-copy';
import styles from './Legal.module.css';

/**
 * SOZLESME SIHIRBAZI — once aydinlatma, sonra kullanim kosullari.
 *
 * IKI ADIM AYRI, cunku KVKK'da iki AYRI yukumluluk var ve fiiller farkli:
 *   1. AYDINLATMA (md.10) -> "Okudum, Bilgilendirildim". Bu bir RIZA DEGIL;
 *      isleme dayanaklari sozlesmenin ifasi / hukuki yukumluluk / mesru menfaat.
 *      Ekranda "Kabul ediyorum" yazmak, dayanagi sozlesme olan bir isleme riza
 *      aliniyormus gorunumu yaratir ve HER IKI kaydi tartismali hale getirir.
 *   2. SOZLESME -> "Kabul Ediyorum". Bu gercekten bir sozlesmedir.
 * Fiiller LEGAL_COPY'den geliyor (mobil ile ayni kaynak); i18n katalogundan
 * DEGIL — cevrilebilir arayuz metni degil, hukuki bir karar.
 *
 * Adim, depodaki onay durumundan TURETILIR; ayri bir "adim" durumu tutulmaz.
 * Tutulsaydi, sayfa yenilendiginde onaylanmis bir metin tekrar sorulabilirdi.
 */
export function ConsentWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snapshot = useSessionStore((state) => state.snapshot);
  const markConsent = useSessionStore((state) => state.markConsent);
  const signOut = useSessionStore((state) => state.signOut);

  const [reachedEnd, setReachedEnd] = useState(false);
  const [saving, setSaving] = useState(false);
  const titleId = useId();

  const step = useMemo(() => {
    if (!snapshot) return null;
    if (!snapshot.consents.kvkkAccepted) return 'kvkk' as const;
    if (!snapshot.consents.usageAgreementAccepted) return 'usage' as const;
    return null;
  }, [snapshot]);

  const onReachEnd = useCallback(() => {
    setReachedEnd(true);
  }, []);

  if (!snapshot) return null;

  // Ikisi de onaylandi: sihirbazin isi bitti.
  if (!step) return <Navigate to={zoneHome(snapshot)} replace />;

  const copy = step === 'kvkk' ? LEGAL_COPY.kvkk : LEGAL_COPY.usage;
  const text = step === 'kvkk' ? KVKK_TEXT : USAGE_AGREEMENT_TEXT;

  const onAccept = async (): Promise<void> => {
    setSaving(true);
    try {
      const response =
        step === 'kvkk'
          ? await postAcceptKvkk(snapshot.user.id)
          : await postAcceptUsage(snapshot.user.id);
      if (response.code !== 'success') {
        toast({ message: response.message ?? copy.errorMessage, tone: 'danger' });
        return;
      }
      markConsent(step);
      // Bir sonraki adim icin okuma sarti SIFIRLANIR — ikinci metin de sonuna
      // kadar okunmali.
      setReachedEnd(false);
      toast({ message: copy.successMessage, tone: 'success' });
    } catch (error) {
      toast({ message: authErrorMessage(error, t), tone: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding="lg" className={styles.card}>
      <p className={styles.step}>
        {t('legal.step', { current: step === 'kvkk' ? 1 : 2, total: 2 })}
      </p>
      <h1 className={styles.title} id={titleId}>
        {copy.title}
      </h1>
      <p className={styles.subtitle}>{copy.subtitle}</p>

      <LegalReader text={text} onReachEnd={onReachEnd} labelledBy={titleId} />

      <p className={reachedEnd ? styles.readOk : styles.readWarn} role="status">
        {reachedEnd ? copy.scrolledEndLabel : copy.incompleteDisclaimer}
      </p>

      <Button
        size="lg"
        block
        disabled={!reachedEnd}
        loading={saving}
        onClick={() => void onAccept()}
      >
        {copy.buttonLabel}
      </Button>

      <p className={styles.note}>{copy.completeDisclaimer}</p>

      <Button
        tone="ghost"
        size="sm"
        block
        onClick={() => {
          // Onaylamadan cikmak MUMKUN olmali (zorlanmis riza gecersizdir);
          // ama uygulamaya giris yok — oturum kapanir.
          void signOut().then(() => navigate('/giris', { replace: true }));
        }}
      >
        {t('auth.backToLogin')}
      </Button>
    </Card>
  );
}
